import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Mail, 
  Send, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Settings,
  RefreshCw,
  Cloud,
  Key
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';


const API = "/api";

const EmailIntegrationSettings = () => {
  const [emailConfig, setEmailConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const { toast } = useToast();

  const fetchEmailConfig = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/email-config`);
      setEmailConfig(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch email configuration",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const testEmailConfig = async () => {
    if (!testEmail.trim()) {
      toast({
        title: "Error",
        description: "Please enter a test email address",
        variant: "destructive",
      });
      return;
    }

    setTestLoading(true);
    try {
      const response = await axios.post(`${API}/admin/test-email`, null, {
        params: { test_email: testEmail }
      });
      
      const result = response.data;
      if (result.success) {
        toast({
          title: "Success",
          description: result.message,
        });
      } else {
        toast({
          title: "Test Failed",
          description: result.error,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send test email",
        variant: "destructive",
      });
    } finally {
      setTestLoading(false);
    }
  };

  useEffect(() => {
    fetchEmailConfig();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            <h3 className="text-lg font-semibold">Email Integration</h3>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin mr-2" />
            Loading email configuration...
          </div>
        </CardContent>
      </Card>
    );
  }

  const getStatusBadge = () => {
    if (!emailConfig) return null;
    
    if (emailConfig.configured) {
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200">
          <CheckCircle className="w-3 h-3 mr-1" />
          Configured
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-red-100 text-red-800 border-red-200">
          <XCircle className="w-3 h-3 mr-1" />
          Not Configured
        </Badge>
      );
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            <h3 className="text-lg font-semibold">Email Integration</h3>
          </div>
          {getStatusBadge()}
        </div>
        <p className="text-sm text-slate-600">
          Microsoft Graph API integration for email notifications when users are tagged in notes
        </p>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {emailConfig && (
          <>
            {/* Service Type */}
            <div className="flex items-center gap-2">
              <Cloud className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-slate-900">Service Type:</span>
              <Badge variant="outline" className="text-blue-700 border-blue-200">
                {emailConfig.type || 'Microsoft Graph API'}
              </Badge>
            </div>

            {/* Current Configuration */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-900">Current Configuration</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-xs text-slate-500 flex items-center gap-1">
                    <Key className="w-3 h-3" />
                    Application ID
                  </Label>
                  <div className="font-mono bg-slate-50 p-2 rounded">
                    {emailConfig.graph_client_id || 'Not set'}
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 flex items-center gap-1">
                    <Key className="w-3 h-3" />
                    Tenant ID
                  </Label>
                  <div className="font-mono bg-slate-50 p-2 rounded">
                    {emailConfig.graph_tenant_id || 'Not set'}
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Sender Email</Label>
                  <div className="font-mono bg-slate-50 p-2 rounded">
                    {emailConfig.sender_email || 'Not set'}
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Sender Name</Label>
                  <div className="font-mono bg-slate-50 p-2 rounded">
                    {emailConfig.sender_name || 'Not set'}
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Credentials Set</Label>
                  <div className="font-mono bg-slate-50 p-2 rounded">
                    {emailConfig.credentials_set ? '✅ Configured' : '❌ Missing'}
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Token Status</Label>
                  <div className="font-mono bg-slate-50 p-2 rounded">
                    {emailConfig.token_obtained ? '✅ Valid' : '❌ Invalid'}
                  </div>
                </div>
              </div>
            </div>

            {/* Configuration Issues */}
            {!emailConfig.configured && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-yellow-800">Configuration Required</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      Email notifications are disabled because the Microsoft Graph API configuration is incomplete.
                      {emailConfig.error && ` Error: ${emailConfig.error}`}
                    </p>
                    <div className="mt-3 text-sm text-yellow-700">
                      <p><strong>To fix this:</strong></p>
                      <ol className="list-decimal list-inside mt-1 space-y-1">
                        <li>Set the GRAPH_CLIENT_ID, GRAPH_CLIENT_SECRET, and GRAPH_TENANT_ID environment variables</li>
                        <li>Ensure the Microsoft Entra app has proper API permissions (Mail.Send)</li>
                        <li>Restart the backend service</li>
                        <li>Test the configuration using the test email feature below</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Test Email Configuration */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-900">Test Email Configuration</h4>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input
                    type="email"
                    placeholder="Enter email address to test..."
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    disabled={!emailConfig.configured}
                  />
                </div>
                <Button
                  onClick={testEmailConfig}
                  disabled={testLoading || !emailConfig.configured || !testEmail.trim()}
                  className="flex items-center gap-2"
                >
                  {testLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {testLoading ? 'Sending...' : 'Send Test'}
                </Button>
              </div>
              {!emailConfig.configured && (
                <p className="text-xs text-slate-500">
                  Configure Microsoft Graph API settings first to enable email testing
                </p>
              )}
            </div>

            {/* Features Enabled */}
            <div className="space-y-3">
              <h4 className="font-medium text-slate-900">Email Features</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  {emailConfig.configured ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <XCircle className="w-4 h-4 text-slate-400" />
                  )}
                  <span className={emailConfig.configured ? 'text-slate-900' : 'text-slate-500'}>
                    User tagging notifications in timeline notes (via Microsoft Graph)
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  {emailConfig.configured ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <XCircle className="w-4 h-4 text-slate-400" />
                  )}
                  <span className={emailConfig.configured ? 'text-slate-900' : 'text-slate-500'}>
                    Message notifications (via Microsoft Graph)
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  {emailConfig.configured ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <XCircle className="w-4 h-4 text-slate-400" />
                  )}
                  <span className={emailConfig.configured ? 'text-slate-900' : 'text-slate-500'}>
                    OAuth2 authentication with automatic token refresh
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  {emailConfig.configured ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <XCircle className="w-4 h-4 text-slate-400" />
                  )}
                  <span className={emailConfig.configured ? 'text-slate-900' : 'text-slate-500'}>
                    HTML and text email format support
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default EmailIntegrationSettings;